/**
 * tabstopps.ts — frei setzbare Tabstopps wie in Outlook (Nachtrag zu T-346).
 *
 * Reine Logik ohne DOM-Messung: das Format, in dem die Stopps stehen, und die
 * Regel, wohin ein Tab springt. Das Messen und Ausrichten im Editor macht
 * `tabAusrichtung.ts`, das Lineal `TabLineal.tsx`.
 *
 * ══ EIN FORMAT FÜR ALLES: DAS VON WORD ═════════════════════════════════════
 * Outlook Desktop zeigt HTML-Mails mit dem Word-Renderer an. Der kennt
 * Tabstopps als CSS-Eigenschaft am Absatz:
 *
 *     <div style="tab-stops:70.9pt center 212.6pt right 425.2pt">
 *
 * Genau dieser Wert steht im Editor im Attribut `data-tabstopps` und beim
 * Speichern bzw. Senden wieder als `tab-stops` im Style (`htmlFuerAusgabe`).
 * Warum nicht gleich im Style? Der Browser kennt `tab-stops` nicht und wirft
 * die Eigenschaft weg, sobald jemand den Style per Skript ändert (Einzug,
 * Schrift, Pinsel …). Ein `data-`Attribut fasst er nicht an — und beim Enter
 * nimmt er es in den neuen Absatz mit, wie Word seine Tabstopps.
 *
 * Andere Mailprogramme (Outlook Web, Gmail, Handy) kennen `tab-stops` nicht.
 * Dort hilft `tab-size` an der Tab-Hülle, das der Editor ohnehin setzt.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export type TabArt = "links" | "zentriert" | "rechts" | "dezimal";

export type Tabstopp = { pt: number; art: TabArt };

/** Abstand der Standard-Tabstopps: 1,25 cm wie in Outlook/Word (deutsch). */
export const STANDARD_ABSTAND_PT = (1.25 / 2.54) * 72;

/** Raster des Lineals: 0,25 cm. Mit gedrückter Alt-Taste frei. */
export const RASTER_PT = (0.25 / 2.54) * 72;

/** Reihenfolge des Umschalters am Lineal — wie in Word. */
export const TAB_ARTEN: readonly TabArt[] = ["links", "zentriert", "rechts", "dezimal"];

export const TAB_ART_NAME: Record<TabArt, string> = {
    links: "linksbündig",
    zentriert: "zentriert",
    rechts: "rechtsbündig",
    dezimal: "am Komma",
};

const WORD_ART: Record<TabArt, string | null> = {
    links: null,
    zentriert: "center",
    rechts: "right",
    dezimal: "decimal",
};

const AUS_WORD: Record<string, TabArt> = {
    left: "links",
    center: "zentriert",
    right: "rechts",
    decimal: "dezimal",
};

/** Füllzeichen (Punkte, Striche …) — Word schreibt sie vor die Ausrichtung. */
const FUELLZEICHEN = new Set(["dotted", "dashed", "heavy", "lined", "middot", "underline", "none"]);

const EINHEIT_PT: Record<string, number> = { pt: 1, in: 72, cm: 72 / 2.54, mm: 72 / 25.4, px: 0.75, pc: 12 };

/** pt mit einer Nachkommastelle, wie Word sie schreibt. */
export const rundePt = (pt: number): number => Math.round(pt * 10) / 10;

export const ptZuCm = (pt: number): number => (pt / 72) * 2.54;

/** „5,25 cm" — für Lineal-Beschriftungen und Ansagen. */
export function cmText(pt: number): string {
    return `${(Math.round(ptZuCm(pt) * 100) / 100).toLocaleString("de-DE")} cm`;
}

/**
 * Liest den Wert von `tab-stops` (bzw. `data-tabstopps`). Unbekanntes wird
 * übergangen: Leiste-Tabs (`bar`) zeichnen in Word nur eine senkrechte Linie,
 * `list`-Tabs gehören zur Nummerierung — beide verschieben keinen Text hier.
 */
export function liesTabstopps(wert: string | null | undefined): Tabstopp[] {
    if (!wert) return [];
    const stopps: Tabstopp[] = [];
    let art: TabArt = "links";
    let ueberspringen = false;
    for (const roh of wert.trim().toLowerCase().split(/[\s,]+/)) {
        if (!roh) continue;
        if (roh in AUS_WORD) {
            art = AUS_WORD[roh];
            continue;
        }
        if (roh === "bar" || roh === "list") {
            ueberspringen = true;
            continue;
        }
        if (FUELLZEICHEN.has(roh)) continue;
        const m = /^(-?\d*\.?\d+)(pt|in|cm|mm|px|pc)?$/.exec(roh);
        if (m) {
            const pt = parseFloat(m[1]) * EINHEIT_PT[m[2] ?? "pt"];
            if (!ueberspringen && Number.isFinite(pt) && pt > 0) stopps.push({ pt: rundePt(pt), art });
        }
        art = "links";
        ueberspringen = false;
    }
    return ordne(stopps);
}

/** Schreibt Stopps im Word-Format: `70.9pt center 212.6pt`. */
export function schreibeTabstopps(stopps: readonly Tabstopp[]): string {
    return ordne(stopps)
        .map((s) => {
            const art = WORD_ART[s.art];
            return `${art ? art + " " : ""}${rundePt(s.pt)}pt`;
        })
        .join(" ");
}

/** Nach Position sortiert; zwei Stopps an derselben Stelle gibt es nicht (der spätere gewinnt). */
function ordne(stopps: readonly Tabstopp[]): Tabstopp[] {
    const nachPos = new Map<number, Tabstopp>();
    for (const s of stopps) nachPos.set(rundePt(s.pt), { pt: rundePt(s.pt), art: s.art });
    return [...nachPos.values()].sort((a, b) => a.pt - b.pt);
}

/** Setzt einen Stopp; ein Stopp an (fast) derselben Stelle wird ersetzt. */
export function stoppSetzen(stopps: readonly Tabstopp[], neu: Tabstopp): Tabstopp[] {
    return ordne([...stopps.filter((s) => Math.abs(s.pt - neu.pt) > 0.5), neu]);
}

/** Entfernt den Stopp an `pt` (Toleranz einen halben Punkt). */
export function stoppEntfernen(stopps: readonly Tabstopp[], pt: number): Tabstopp[] {
    return stopps.filter((s) => Math.abs(s.pt - pt) > 0.5);
}

/**
 * Wert von `tab-stops` aus einem rohen Style-Attribut — `null`, wenn keiner
 * da ist. Bewusst über den Text: Das CSSOM des Browsers hat die Eigenschaft
 * gar nicht erst eingelesen.
 */
export function tabStoppsAusStyle(style: string | null | undefined): string | null {
    if (!style) return null;
    const m = /(?:^|;)\s*tab-stops\s*:\s*([^;]*)/i.exec(style);
    return m ? m[1].trim() : null;
}

/** Style-Text mit (bzw. ohne, bei leerem `wert`) `tab-stops`. Alles andere bleibt Zeichen für Zeichen. */
export function styleMitTabStopps(style: string | null | undefined, wert: string | null): string {
    const ohne = (style ?? "")
        .split(";")
        .filter((d) => d.trim() !== "" && !/^\s*tab-stops\s*:/i.test(d))
        .map((d) => d.trim())
        .join(";");
    if (!wert) return ohne;
    return `${ohne}${ohne ? ";" : ""}tab-stops:${wert}`;
}

/** Was hinter einem Tab bis zum nächsten Tab bzw. Zeilenende steht — in pt. */
export type Abschnitt = {
    /** Breite des ganzen Abschnitts. */
    breite: number;
    /** Breite bis vor das erste Komma; `null`, wenn keins drin ist. */
    bisKomma: number | null;
};

/** Mindestbreite eines Tabs. Darunter springt er weiter (CSS: ½ Zeichen). */
export const MIN_TAB_PT = 2;

/**
 * Wohin ein Tab springt — die Position, an der der Text dahinter BEGINNT.
 *
 * Wie in Word:
 *  - Der nächste eigene Stopp rechts von `x` gilt.
 *  - Rechts vom letzten eigenen Stopp gelten die Standard-Stopps (alle
 *    1,25 cm). Links davon nicht — ein eigener Stopp löscht die Standard-
 *    Stopps vor ihm.
 *  - Rechts-, Zentriert- und Komma-Stopps richten den folgenden Abschnitt
 *    am Stopp aus. Passt er nicht mehr dazwischen, gilt der nächste Stopp.
 *
 * Alle Werte in pt, gemessen vom linken Rand der Schreibfläche.
 */
export function tabZiel(
    x: number,
    stopps: readonly Tabstopp[],
    abschnitt: Abschnitt,
    abstand: number = STANDARD_ABSTAND_PT,
): number {
    for (const s of stopps) {
        if (s.pt <= x) continue;
        const ziel = ausrichten(s, abschnitt);
        if (ziel - x >= MIN_TAB_PT) return ziel;
    }
    const letzter = stopps.length ? stopps[stopps.length - 1].pt : 0;
    const ab = Math.max(x + MIN_TAB_PT, letzter + 0.01);
    return Math.ceil(ab / abstand - 1e-9) * abstand;
}

function ausrichten(s: Tabstopp, a: Abschnitt): number {
    switch (s.art) {
        case "rechts":
            return s.pt - a.breite;
        case "zentriert":
            return s.pt - a.breite / 2;
        case "dezimal":
            return s.pt - (a.bisKomma ?? a.breite);
        default:
            return s.pt;
    }
}
